import React, { useMemo } from 'react';
import {Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip} from '@mui/material';
import {NAVY, PageHeader, BandChip, GuidanceBanner, bandColor, headCell, bodyCell} from '../components/widgets.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import { useDashboard } from '../../../context/DashboardContext';
import { rowsFrom, labelCase } from '../schoolData';
import {useTableControls} from '../../DashboardCommon/useTableControls';
import {SortFilterHeaderCell} from '../../DashboardCommon/SortFilterHeaderCell.comp';
import {TableToolbar} from '../../DashboardCommon/TableToolbar.comp';

export const CompletionBar = ({label, pct, status, threshold = 75}) => (
    <Box sx={{mb: 3}}>
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75}}>
            <Typography sx={{fontSize: '14px', fontWeight: 700, color: NAVY}}>{label}</Typography>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Typography sx={{fontSize: '15px', fontWeight: 800, color: NAVY}}>{pct}%</Typography>
                <BandChip label={status}/>
            </Box>
        </Box>
        <Box sx={{position: 'relative', height: 24, borderRadius: '12px', backgroundColor: 'var(--brand-surface-muted, #F3F4F6)'}}>
            <Tooltip title={`${pct}%`} arrow placement="top">
                <Box sx={{position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, height: '100%', backgroundColor: bandColor(status), borderRadius: '12px', overflow: 'hidden', cursor: 'pointer'}}/>
            </Tooltip>
            <Tooltip title={`Threshold: ${threshold}%`} arrow placement="top">
                <Box sx={{
                    position: 'absolute', left: `${threshold}%`, top: '-9px', width: 0, height: 0,
                    borderLeft: '6px solid transparent', borderRight: '6px solid transparent',
                    borderTop: '9px solid #374151',
                    transform: 'translateX(-50%)', cursor: 'pointer', zIndex: 4,
                }}/>
            </Tooltip>
        </Box>
    </Box>
);

const REGIONS = [
    {region: 'North East', rate: '68%', action: 'Follow up'},
    {region: 'N. Ireland', rate: '61%', action: 'Follow up'},
    {region: 'Wales',      rate: '77%', action: 'OK'},
    {region: 'Midlands',   rate: '82%', action: 'OK'},
];

export const RegionsTable = () => (
    <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%'}}>
        <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 2}}>Under-represented regions</Typography>
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={headCell}>Region</TableCell>
                        <TableCell sx={{...headCell, textAlign: 'center'}}>Rate</TableCell>
                        <TableCell sx={{...headCell, textAlign: 'center'}}>Action</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {REGIONS.map((r) => (
                        <TableRow key={r.region}>
                            <TableCell sx={{...bodyCell, fontWeight: 700, color: NAVY}}>{r.region}</TableCell>
                            <TableCell sx={{...bodyCell, textAlign: 'center', fontWeight: 700}}>{r.rate}</TableCell>
                            <TableCell sx={{...bodyCell, textAlign: 'center'}}><BandChip label={r.action}/></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
);

const panel = {flex: 1, border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3};

/*
 * SD-30-02 — the class table beside the grade chart (DataMap Deck slide 66 /
 * SD slide 30: "Replace grade table w/ class table"). Columns as the Data Map
 * lists them — class · participants · registered · rate % — with the rate's
 * band chip under it, as on the slide. Read from S_13 C_3 "Participation by
 * class"; the class is shown with its grade, since class names repeat across
 * grades, and sorts and filters by that label. A missing value shows "—".
 * Every column sorts from its header; filters appear once there are enough
 * classes (QA 9 Oct 2026).
 */
const classLabel = (r) => (r.grade && r.grade !== '—' ? `${labelCase(r.klass)} (${labelCase(r.grade)})` : labelCase(r.klass));
const CLASS_COLUMNS = [
    {key: 'klass', label: 'Class', type: 'text', accessor: classLabel},
    {key: 'participants', label: 'Participants', type: 'number'},
    {key: 'registered', label: 'Registered', type: 'number'},
    {key: 'rate', label: 'Rate', type: 'percent'},
];

const ClassParticipationTable = ({card}) => {
    const rows = rowsFrom(card, {
        grade: 'Grade', klass: 'Class', participants: 'Participants', registered: 'Registered', rate: ['Rate %', 'Rate'],
    });
    const t = useTableControls(rows, CLASS_COLUMNS);
    if (!rows.length) return null;
    const center = {...bodyCell, textAlign: 'center'};
    return (
        <Box sx={panel}>
            <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 2}}>{card.title || 'Participation by class'}</Typography>
            {(t.chips.length > 0 || rows.length >= 5) && <TableToolbar {...t}/>}
            <TableContainer sx={{overflowX: 'auto'}}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {CLASS_COLUMNS.map((c, i) => (
                                <SortFilterHeaderCell key={c.key} {...t.headerProps(c.key)} sx={{...headCell, textAlign: i === 0 ? 'left' : 'center'}}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {t.rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} sx={{...bodyCell, textAlign: 'center', color: '#6A7282', py: 3}}>No class matches the filters.</TableCell>
                            </TableRow>
                        )}
                        {t.rows.map((r) => {
                            const band = r._cell.rate?.band;
                            return (
                                <TableRow key={`${r.grade}-${r.klass}-${rows.indexOf(r)}`}>
                                    <TableCell sx={{...bodyCell, textAlign: 'left', fontWeight: 700, color: NAVY}}>
                                        {classLabel(r)}
                                    </TableCell>
                                    <TableCell sx={center}>{r.participants}</TableCell>
                                    <TableCell sx={center}>{r.registered}</TableCell>
                                    <TableCell sx={center}>
                                        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5}}>
                                            <span>{r.rate}</span>
                                            {band && <BandChip label={band}/>}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const PARTICIPATION_BY_GRADE_SECTION_CODE = 'S_13';
const CARD_SECTION = PARTICIPATION_BY_GRADE_SECTION_CODE;

const ParticipationByGradeTab = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTION });

    const s13Section = useMemo(() => {
        return dashboardData?.cards?.sections?.find(
            (section) => section.section_code === PARTICIPATION_BY_GRADE_SECTION_CODE
        );
    }, [dashboardData]);

    if (loading) {
        return <CircularLoader minHeight="200px" />;
    }

    if (!s13Section) {
        return (
            <NoDataAvailable />
        );
    }

    const cards = s13Section.cards || [];

    // C_1: TitleCard
    const titleCard = cards.find(c => c.card_code === 'C_1');
    const headerTitle = titleCard?.title || "Participation by Grade";
    const headerSubtitle = titleCard?.footer || "Which grades have not participated enough for us to trust the results?";
    const eyebrow = ''; //titleCard?.header;

    // C_2: BarChartBMCard
    const barChartCard = cards.find(c => c.card_code === 'C_2');
    const items = barChartCard?.items || [];
    const threshold = items[0]?.benchmark_values?.find(b => b.name === 'Threshold')?.value ?? 75;

    const bannerText = `Grades below ${threshold}% participation need follow-up before their scores are treated as representative.`;

    return (
        <Box>
            <PageHeader
                eyebrow={eyebrow || ''}
                title={headerTitle}
                subtitle={headerSubtitle}
            />

            {/* Two columns, as the Network "Participation by School" tab: the grade bars on the left, the
                class table on the right; stacked on a phone (QA 9 Oct 2026, LA-SD-4). */}
            <Box sx={{display: 'flex', gap: 2.5, alignItems: 'stretch', flexDirection: {xs: 'column', md: 'row'}, mb: 3}}>
                <Box sx={panel}>
                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, mb: 3}}>
                        {barChartCard?.title || "Completion Rate by Grade"} · {threshold}% threshold
                    </Typography>
                    {items.map((item, idx) => (
                        <CompletionBar
                            key={idx}
                            label={labelCase(item.label)}
                            pct={item.value !== undefined && item.value !== null ? Math.round(item.value) : 0}
                            status={item.band || item.data?.band}
                            threshold={threshold}
                        />
                    ))}
                </Box>

                <ClassParticipationTable card={cards.find(c => c.card_code === 'C_3')}/>
            </Box>

            <GuidanceBanner label="Recommended action:" text={bannerText}/>
        </Box>
    );
};

export default ParticipationByGradeTab;
